import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import Lenis from "lenis";
import {
  BookOpen,
  Sparkles,
  Feather,
  History,
  ChevronDown,
  Moon,
  Compass,
  Maximize2,
  Send,
  CheckCircle2,
  Star,
  ArrowRight,
  RefreshCw,
  Library,
  Kanban,
  Brain,
  FileOutput,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookCanvas } from "./book-canvas";

// Suppress unused import warnings — kept for parity with source
void BookOpen;
void Send;

// Register ScrollTrigger and MotionPath plugins with GSAP
gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

export const CinematicLanding: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // --- STATE FOR INTERACTIVES ---
  const [typedText, setTypedText] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [storyContext, setStoryContext] = useState<string[]>([]);
  const [isBetaSubmitted, setIsBetaSubmitted] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [lunarDate, setLunarDate] = useState(
    t("cinematicLanding.lunarPhaseWaxingGibbous", "Fase: Lua Crescente (72% visível)")
  );
  const [activeChapter, setActiveChapter] = useState(1);

  // --- REFS ---
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  // References to the ThreeJS elements created in BookCanvas
  const threeObjectsRef = useRef<{
    bookGroup: THREE.Group | null;
    frontCover: THREE.Group | null;
    backCover: THREE.Group | null;
    pagesGroup: THREE.Group | null;
    singlePages: THREE.Group[];
    moonMesh: THREE.Mesh | null;
    pointLight: THREE.PointLight | null;
    starField: THREE.Points | null;
  }>({
    bookGroup: null,
    frontCover: null,
    backCover: null,
    pagesGroup: null,
    singlePages: [],
    moonMesh: null,
    pointLight: null,
    starField: null,
  });

  const typingRef = useRef({ charCount: 0 });

  // Story paragraph to be typed automatically via GSAP timeline scroll
  const autoStoryText = t(
    "cinematicLanding.autoStoryText",
    "A névoa matinal ainda pairava sobre a cidade quando ela abriu o documento em branco. Dedos hesitantes sobre o teclado — a primeira frase sempre foi a mais difícil. Mas desta vez havia algo diferente: o assistente conhecia cada personagem, cada lugar, cada fio solto da trama. E quando ela digitou a primeira palavra, a continuação surgiu como se o próprio livro soubesse aonde queria chegar."
  );

  const aiPresetSuggestion = t(
    "cinematicLanding.aiPresetSuggestion",
    "...ela percebeu que o assistente havia lembrado do detalhe que ela escrevera três capítulos atrás — o nome da rua, a cor do casaco — e teceu tudo de volta com precisão cirúrgica."
  );

  // --- COMPONENT LIFECYCLE / LUNAR CLOCK ---
  useEffect(() => {
    const today = new Date();
    const cycleDay = (today.getDate() + 15) % 29.53;
    let phase = "";
    if (cycleDay < 1.84) phase = t("cinematicLanding.lunarPhaseNew", "Nova (0% visível)");
    else if (cycleDay < 5.53) phase = t("cinematicLanding.lunarPhaseWaxingCrescent", "Crescente Côncava (18% visível)");
    else if (cycleDay < 9.22) phase = t("cinematicLanding.lunarPhaseFirstQuarter", "Quarto Crescente (50% visível)");
    else if (cycleDay < 12.91) phase = t("cinematicLanding.lunarPhaseWaxingGibbous", "Crescente Gibosa (72% visível)");
    else if (cycleDay < 16.6) phase = t("cinematicLanding.lunarPhaseFull", "Cheia (100% brilhante!)");
    else if (cycleDay < 20.29) phase = t("cinematicLanding.lunarPhaseWaningGibbous", "Minguante Gibosa (75% visível)");
    else if (cycleDay < 23.98) phase = t("cinematicLanding.lunarPhaseLastQuarter", "Quarto Minguante (50% visível)");
    else if (cycleDay < 27.67) phase = t("cinematicLanding.lunarPhaseWaningCrescent", "Minguante Côncava (20% visível)");
    else phase = t("cinematicLanding.lunarPhaseNew", "Nova (0% visível)");

    setLunarDate(t("cinematicLanding.lunarDateLabel", "Fase Lunar: {{phase}} • reformu.lat", { phase }));
  }, [t]);

  // --- CORE ANIMATION: LENIS + GSAP TIMELINE ---
  useEffect(() => {
    if (!scrollTrackRef.current || !viewportRef.current) return;

    // 1. Initialize Lenis Smooth Scroll
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1.05,
    });

    const raf = (time: number) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    // Synchronize Lenis with ScrollTrigger
    lenis.on("scroll", ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // 2. Main Scroll-driven Master GSAP Timeline
    const masterTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: scrollTrackRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: 1.2,
        pin: viewportRef.current,
        anticipatePin: 1,
      },
    });

    // --- TIMELINE LABELS DEFINING THE 7 SCENES ---
    masterTimeline.addLabel("scene1", 0);   // Hero Inicial
    masterTimeline.addLabel("scene2", 1.5); // Entrada na Experiência
    masterTimeline.addLabel("scene3", 3);   // Ideia virando Estrutura
    masterTimeline.addLabel("scene4", 4.5); // Mockup do App
    masterTimeline.addLabel("scene5", 6);   // Livro Lunar 3D Aberto
    masterTimeline.addLabel("scene6", 7.5); // Bento de Benefícios
    masterTimeline.addLabel("scene7", 9);   // Eclipse / CTA Final

    // ==========================================
    // CENA 1: Hero Inicial -> CENA 2 (0.0 to 1.5)
    // ==========================================
    masterTimeline.to(
      ".scene1-hero-title",
      { y: -120, opacity: 0, filter: "blur(12px)", scale: 0.94, duration: 1.2 },
      "scene1"
    );
    masterTimeline.to(
      ".scene1-scroll-indicator",
      { opacity: 0, y: 30, duration: 0.8 },
      "scene1"
    );

    // ThreeJS: Move Moon to upper-right and scale slightly
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const moon = threeObjectsRef.current.moonMesh;
          if (moon) {
            const progress = masterTimeline.scrollTrigger?.progress || 0;
            const factor = Math.min(progress / 0.15, 1);
            moon.position.set(2.2 * factor, 0.5 + 0.8 * factor, -6 + 1.5 * factor);
            moon.scale.set(1 + 0.2 * factor, 1 + 0.2 * factor, 1 + 0.2 * factor);
          }
        },
        duration: 1.5,
      },
      "scene1"
    );

    // ThreeJS: Bring Book closer and rotate to elegant 3D tilt
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const book = threeObjectsRef.current.bookGroup;
          if (book) {
            const progress = masterTimeline.scrollTrigger?.progress || 0;
            const factor = Math.min(progress / 0.15, 1);
            book.position.set(-1.0 * factor, 0.1 * factor, -1 + 1.8 * factor);
            book.rotation.set(
              0.3 - 0.1 * factor,
              -0.4 + 0.8 * factor,
              0.15 - 0.1 * factor
            );
          }
        },
        duration: 1.5,
      },
      "scene1"
    );

    // CSS 3D: Flying manuscript sheets entering from deep perspective
    masterTimeline.fromTo(
      ".floating-sheet-1",
      { translateZ: -800, rotateX: 60, rotateY: 20, opacity: 0 },
      { translateZ: 350, rotateX: -20, rotateY: -10, opacity: 1, duration: 1.5 },
      "scene1"
    );
    masterTimeline.fromTo(
      ".floating-sheet-2",
      { translateZ: -1000, rotateX: -45, rotateY: -35, opacity: 0 },
      { translateZ: 250, rotateX: 15, rotateY: 15, opacity: 1, duration: 1.5 },
      "scene1"
    );

    // ==========================================
    // CENA 2: Entrada na Experiência -> CENA 3 (1.5 to 3.0)
    // ==========================================
    masterTimeline.to(
      ".floating-sheet-1",
      { x: -350, y: -130, translateZ: 0, rotateX: 8, rotateY: 18, rotateZ: -5, scale: 0.82, duration: 1.5 },
      "scene2"
    );
    masterTimeline.to(
      ".floating-sheet-2",
      { x: -350, y: 130, translateZ: 0, rotateX: -6, rotateY: 18, rotateZ: 5, scale: 0.82, duration: 1.5 },
      "scene2"
    );
    // Make card 3 fly in
    masterTimeline.fromTo(
      ".floating-sheet-3",
      { x: -800, opacity: 0, rotateY: 45 },
      { x: -350, y: 0, opacity: 1, rotateX: 0, rotateY: 18, rotateZ: 0, scale: 0.82, duration: 1.5 },
      "scene2"
    );

    // Fade in Scene 3 narrative text on the right
    masterTimeline.fromTo(
      ".scene3-narrative-box",
      { opacity: 0, x: 80, filter: "blur(6px)" },
      { opacity: 1, x: 0, filter: "blur(0px)", duration: 1.2 },
      "scene2+=0.3"
    );

    // Animate SVG constellation pathway
    masterTimeline.fromTo(
      ".constellation-path",
      { strokeDashoffset: 1200 },
      { strokeDashoffset: 0, duration: 1.5, ease: "power2.out" },
      "scene2"
    );

    // Trace AI Spark along the constellation
    masterTimeline.fromTo(
      ".constellation-spark",
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.3 },
      "scene2"
    );
    masterTimeline.to(
      ".constellation-spark",
      {
        motionPath: {
          path: ".constellation-path",
          autoRotate: false,
        },
        duration: 1.5,
      },
      "scene2"
    );

    // ==========================================
    // CENA 3: Ideia virando Estrutura -> CENA 4 (3.0 to 4.5)
    // ==========================================
    masterTimeline.to(
      ".floating-sheet-1, .floating-sheet-2, .floating-sheet-3",
      { scale: 0.3, opacity: 0, x: 0, y: 0, filter: "blur(15px)", duration: 1.2 },
      "scene3"
    );
    masterTimeline.to(
      ".constellation-svg",
      { opacity: 0, scale: 0.9, filter: "blur(8px)", duration: 1.0 },
      "scene3"
    );
    masterTimeline.to(
      ".scene3-narrative-box",
      { opacity: 0, y: -40, filter: "blur(6px)", duration: 0.9 },
      "scene3"
    );

    // Scale up and float the main premium app mockup
    masterTimeline.fromTo(
      ".app-editor-mockup",
      { scale: 0.72, y: 350, opacity: 0, rotateX: 18, filter: "blur(4px)" },
      { scale: 1.0, y: 0, opacity: 1, rotateX: 0, filter: "blur(0px)", duration: 1.5 },
      "scene3+=0.2"
    );

    // ThreeJS: Push book slightly to left background, standing closed
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const book = threeObjectsRef.current.bookGroup;
          if (book) {
            const progress = masterTimeline.scrollTrigger?.progress || 0;
            if (progress >= 0.3 && progress <= 0.5) {
              const localFactor = (progress - 0.3) / 0.15;
              book.position.set(-1.0 - 1.8 * localFactor, 0.1 + 0.9 * localFactor, 0.8 - 0.3 * localFactor);
              book.rotation.set(0.2 - 0.15 * localFactor, 0.4 - 0.2 * localFactor, 0.05 - 0.05 * localFactor);
            }
          }
        },
        duration: 1.5,
      },
      "scene3"
    );

    // ==========================================
    // CENA 4: Mockup do App -> CENA 5 (4.5 to 6.0)
    // ==========================================
    masterTimeline.to(
      typingRef.current,
      {
        charCount: autoStoryText.length,
        duration: 1.8,
        ease: "none",
        onUpdate: () => {
          setTypedText(autoStoryText.slice(0, Math.floor(typingRef.current.charCount)));
        },
      },
      "scene4"
    );

    // Reveal AI suggest pop-up with a gold-glowing aura
    masterTimeline.fromTo(
      ".editor-ai-suggestion-box",
      { opacity: 0, scale: 0.85, y: 30, filter: "blur(4px)" },
      { opacity: 1, scale: 1, y: 0, filter: "blur(0px)", duration: 1.0 },
      "scene4+=0.8"
    );

    // ThreeJS: Unfold the covers and bend pages of the 3D Book!
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const progress = masterTimeline.scrollTrigger?.progress || 0;
          if (progress >= 0.45 && progress <= 0.62) {
            const localFactor = (progress - 0.45) / 0.15; // 0 to 1

            const frontCover = threeObjectsRef.current.frontCover;
            const backCover = threeObjectsRef.current.backCover;
            const pagesGroup = threeObjectsRef.current.pagesGroup;
            const singlePages = threeObjectsRef.current.singlePages;

            if (frontCover) frontCover.rotation.y = (Math.PI - 0.15) * localFactor;
            if (backCover) backCover.rotation.y = -(Math.PI - 0.15) * localFactor;

            if (pagesGroup) {
              pagesGroup.visible = localFactor > 0.1;
            }

            singlePages.forEach((page, index) => {
              const startDelay = index * 0.08;
              const pageFactor = Math.max(0, Math.min((localFactor - startDelay) / (1 - startDelay), 1));
              page.rotation.y = -(Math.PI - 0.18 - index * 0.04) * pageFactor;
            });
          }
        },
        duration: 1.5,
      },
      "scene4"
    );

    // ==========================================
    // CENA 5: O Livro Lunar Aberto -> CENA 6 (6.0 to 7.5)
    // ==========================================
    masterTimeline.to(
      ".app-editor-mockup",
      { x: -220, scale: 0.85, opacity: 0.6, duration: 1.5 },
      "scene5"
    );
    masterTimeline.to(
      ".editor-ai-suggestion-box",
      { x: -180, scale: 0.8, opacity: 0.4, duration: 1.5 },
      "scene5"
    );

    // Fade in Scene 5 poetic caption
    masterTimeline.fromTo(
      ".scene5-poetic-title",
      { opacity: 0, y: 40, filter: "blur(6px)" },
      { opacity: 1, y: 0, filter: "blur(0px)", duration: 1.2 },
      "scene5+=0.3"
    );

    // ThreeJS: Center and orbit the opened 3D Book alongside the editor
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const progress = masterTimeline.scrollTrigger?.progress || 0;
          if (progress >= 0.6 && progress <= 0.75) {
            const localFactor = (progress - 0.6) / 0.15;
            const book = threeObjectsRef.current.bookGroup;
            const moon = threeObjectsRef.current.moonMesh;

            if (book) {
              book.position.set(-2.8 + 5.1 * localFactor, 1.0 - 1.1 * localFactor, 0.5 + 1.2 * localFactor);
              book.rotation.set(0.05 + 0.1 * localFactor, 0.2 - 0.7 * localFactor, 0);
            }

            if (moon) {
              moon.position.set(2.2 + 0.1 * localFactor, 1.3 - 1.4 * localFactor, -4.5 + 0.5 * localFactor);
              moon.scale.set(1.2 + 0.3 * localFactor, 1.2 + 0.3 * localFactor, 1.2 + 0.3 * localFactor);
            }
          }
        },
        duration: 1.5,
      },
      "scene5"
    );

    // ==========================================
    // CENA 6: Grade de Benefícios -> CENA 7 (7.5 to 9.0)
    // ==========================================
    masterTimeline.to(
      ".app-editor-mockup",
      { y: -350, scale: 0.5, opacity: 0, filter: "blur(18px)", duration: 1.5 },
      "scene6"
    );
    masterTimeline.to(
      ".editor-ai-suggestion-box",
      { y: -300, scale: 0.5, opacity: 0, filter: "blur(12px)", duration: 1.5 },
      "scene6"
    );
    masterTimeline.to(
      ".scene5-poetic-title",
      { opacity: 0, y: -40, filter: "blur(6px)", duration: 0.9 },
      "scene6"
    );

    // ThreeJS: Scale down the book and lower it elegantly
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const progress = masterTimeline.scrollTrigger?.progress || 0;
          if (progress >= 0.75 && progress <= 0.9) {
            const localFactor = (progress - 0.75) / 0.15;
            const book = threeObjectsRef.current.bookGroup;
            const moon = threeObjectsRef.current.moonMesh;

            if (book) {
              book.position.set(2.3 - 2.3 * localFactor, -0.1 - 3.0 * localFactor, 1.7 - 2.5 * localFactor);
              const frontCover = threeObjectsRef.current.frontCover;
              const backCover = threeObjectsRef.current.backCover;
              if (frontCover) frontCover.rotation.y = (Math.PI - 0.15) * (1 - localFactor * 0.8);
              if (backCover) backCover.rotation.y = -(Math.PI - 0.15) * (1 - localFactor * 0.8);
            }

            if (moon) {
              moon.position.set(2.3 - 2.3 * localFactor, -0.1 + 1.9 * localFactor, -4.0 - 2.0 * localFactor);
              moon.scale.set(1.5, 1.5, 1.5);
            }
          }
        },
        duration: 1.5,
      },
      "scene6"
    );

    // Fade in Bento benefits grid
    masterTimeline.fromTo(
      ".benefits-heading",
      { opacity: 0, y: 50, filter: "blur(6px)" },
      { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.8 },
      "scene6"
    );

    masterTimeline.fromTo(
      ".benefit-item-1",
      { y: 150, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.8 },
      "scene6+=0.1"
    );
    masterTimeline.fromTo(
      ".benefit-item-2",
      { y: 150, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.8 },
      "scene6+=0.25"
    );
    masterTimeline.fromTo(
      ".benefit-item-3",
      { y: 150, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.8 },
      "scene6+=0.4"
    );
    masterTimeline.fromTo(
      ".benefit-item-4",
      { y: 150, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.8 },
      "scene6+=0.55"
    );

    // ==========================================
    // CENA 7: Eclipse / CTA Final (9.0 to 10.0)
    // ==========================================
    masterTimeline.to(
      ".benefits-main-container",
      { opacity: 0, scale: 0.93, y: -80, filter: "blur(14px)", duration: 1.2 },
      "scene7"
    );

    // ThreeJS: Center Moon for a breathtaking solar glow alignment behind CTA
    masterTimeline.to(
      {},
      {
        onUpdate: () => {
          const progress = masterTimeline.scrollTrigger?.progress || 0;
          if (progress >= 0.9) {
            const localFactor = (progress - 0.9) / 0.1;
            const moon = threeObjectsRef.current.moonMesh;
            const book = threeObjectsRef.current.bookGroup;

            if (moon) {
              moon.position.set(0, 0.4, -5);
              moon.scale.set(1.5 + 0.3 * localFactor, 1.5 + 0.3 * localFactor, 1.5 + 0.3 * localFactor);
            }

            if (book) {
              book.position.set(0, -6, -2);
            }
          }
        },
        duration: 1.5,
      },
      "scene7"
    );

    // Rise final cinematic CTA form
    masterTimeline.fromTo(
      ".final-cta-card",
      { y: 380, opacity: 0, scale: 0.94, filter: "blur(6px)" },
      { y: 0, opacity: 1, scale: 1.0, filter: "blur(0px)", duration: 1.4 },
      "scene7+=0.2"
    );

    // Cleanups
    return () => {
      lenis.destroy();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // --- INTERACTION: LIVE AI CO-WRITE ---
  const handleAiInspiration = async () => {
    if (!userPrompt.trim()) return;
    setIsAiLoading(true);
    setAiResponse("");

    try {
      const response = await fetch("/api/demo/cowrite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: userPrompt,
          context: autoStoryText + " " + typedText,
        }),
      });

      const data = await response.json() as { text?: string };
      if (data.text) {
        setAiResponse(data.text);
        setStoryContext((prev) => [...prev, `Autor: ${userPrompt}`, `IA: ${data.text}`]);
      } else {
        setAiResponse(t("cinematicLanding.aiNoResponse", "O luar brilhou intensamente, mas nenhuma resposta fluiu..."));
      }
    } catch (e) {
      console.error("AI write request failed:", e);
      setAiResponse(t("cinematicLanding.aiConnectionError", "A conexão cósmica flutuou. Tente inspirar-se novamente."));
    } finally {
      setIsAiLoading(false);
    }
  };

  // --- INTERACTION: BETA SUBSCRIPTION / NAVIGATE ---
  const handleBetaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (emailInput.trim()) {
      setIsBetaSubmitted(true);
      // After showing success, navigate to app after a brief moment
      setTimeout(() => navigate("/app"), 1800);
    }
  };

  return (
    // Scoped font variables so Cinzel / JetBrains Mono don't override the rest of the app
    <div
      style={
        {
          "--landing-font-serif": '"Cinzel", Georgia, serif',
          "--landing-font-mono": '"JetBrains Mono", monospace',
        } as React.CSSProperties
      }
      className="relative w-full min-h-screen bg-luna-dark text-luna-lavender overflow-x-hidden no-scrollbar selection:bg-luna-glow/30 selection:text-luna-gold"
    >
      {/* 3D background WebGL canvas overlay */}
      <BookCanvas threeObjectsRef={threeObjectsRef} />

      {/* Cinematic ambient background lighting */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
        <div className="absolute top-0 inset-x-0 h-screen aurora-bg opacity-70" />
        <div className="absolute bottom-0 inset-x-0 h-[150vh] aurora-bottom opacity-50 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-luna-purple/5 via-luna-dark/40 to-luna-dark pointer-events-none" />
      </div>

      {/* Premium minimal header floating */}
      <header className="fixed top-0 left-0 w-full z-50 px-6 md:px-12 py-5 flex items-center justify-between border-b border-luna-lavender/5 glass-panel-light">
        <div className="flex items-center gap-3">
          <div className="relative w-9 h-9 rounded-full bg-gradient-to-tr from-luna-purple to-luna-glow flex items-center justify-center border border-luna-gold/25 shadow-lg shadow-luna-purple/40">
            <Moon className="w-5 h-5 text-luna-gold animate-pulse" />
          </div>
          <span
            style={{ fontFamily: "var(--landing-font-serif)" }}
            className="tracking-widest text-lg font-semibold text-white uppercase glow-text-gold"
          >
            reformu.lat
          </span>
        </div>
        <div
          style={{ fontFamily: "var(--landing-font-mono)" }}
          className="hidden md:flex items-center gap-8 text-xs tracking-widest uppercase text-luna-lavender/65"
        >
          <span
            onClick={() => navigate("/features")}
            className="hover:text-luna-gold transition-colors cursor-pointer"
          >
            {t("cinematicLanding.navFeatures", "Recursos")}
          </span>
          <span
            onClick={() => navigate("/pricing")}
            className="hover:text-luna-gold transition-colors cursor-pointer"
          >
            {t("cinematicLanding.navPricing", "Preços")}
          </span>
          <span
            onClick={() => navigate("/marketplace")}
            className="hover:text-luna-gold transition-colors cursor-pointer"
          >
            {t("cinematicLanding.navMarketplace", "Marketplace")}
          </span>
          <span className="text-luna-gold/80 px-2 py-0.5 border border-luna-gold/20 rounded-full bg-luna-gold/5">
            {t("cinematicLanding.freeTierBadge", "Grátis para começar")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div
            style={{ fontFamily: "var(--landing-font-mono)" }}
            className="hidden md:block text-[10px] text-luna-gold/70 select-none border border-luna-lavender/10 rounded-full px-4 py-1.5 bg-luna-dark/80 tracking-wide"
          >
            {t("cinematicLanding.lunarFooterSuffix", "{{lunarDate}} • escreva sob o luar", { lunarDate })}
          </div>
          <button
            onClick={() => navigate("/app")}
            className="px-5 py-2 rounded-full bg-gradient-to-r from-luna-purple to-luna-glow border border-luna-gold/25 text-xs font-semibold text-white tracking-wider uppercase hover:opacity-90 active:scale-95 transition-all cursor-pointer shadow-lg shadow-luna-purple/40"
            style={{ fontFamily: "var(--landing-font-mono)" }}
          >
            {t("cinematicLanding.navEnter", "Entrar")}
          </button>
        </div>
      </header>

      {/* The main scroll wrapper holding the full-length scroll runway */}
      <div ref={scrollTrackRef} className="relative w-full h-[750vh] z-20">
        {/* Pinned visual container that holds the scene compositions */}
        <div ref={viewportRef} className="sticky top-0 w-full h-screen overflow-hidden flex items-center justify-center">

          {/* =========================================================================
              SCENE 1: HERO INICIAL (0% - 15%)
              ========================================================================= */}
          <div className="scene1-hero-title absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-30 select-none">
            <div
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-luna-gold/20 bg-luna-gold/5 text-luna-gold text-xs tracking-[0.25em] uppercase mb-8 shadow-inner animate-pulse"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t("cinematicLanding.heroBadge", "Do rascunho à publicação")}
            </div>

            <h1
              style={{ fontFamily: "var(--landing-font-serif)" }}
              className="text-5xl md:text-8xl tracking-tight leading-none text-white glow-text-gold max-w-4xl mx-auto mb-6"
            >
              {t("cinematicLanding.heroTitleLine1", "Escreva seu livro,")} <br />
              <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-luna-gold via-luna-lavender to-white">
                {t("cinematicLanding.heroTitleLine2", "do rascunho à publicação")}
              </span>
            </h1>

            <p className="font-sans text-sm md:text-lg text-luna-lavender/70 max-w-xl mx-auto leading-relaxed tracking-wide font-light">
              {t(
                "cinematicLanding.heroSubtitle",
                "reformu.lat é o editor com IA que conhece o seu livro — personagens, lugares, lore — e escreve junto com você. Universo, Outline Board, capas geradas por IA e marketplace de profissionais."
              )}
            </p>

            <div
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="scene1-scroll-indicator absolute bottom-12 inset-x-0 flex flex-col items-center gap-2 text-luna-lavender/40 text-[10px] tracking-[0.3em] uppercase"
            >
              <span>{t("cinematicLanding.scrollIndicator", "Role para Iniciar a Cena")}</span>
              <ChevronDown className="w-4 h-4 text-luna-gold animate-bounce" />
            </div>
          </div>

          {/* =========================================================================
              SCENE 2: MANUSCRIPT PARALLAX LAYER (15% - 30% CSS 3D PAPERS)
              ========================================================================= */}
          <div className="absolute inset-0 perspective-container pointer-events-none z-20">
            {/* Paper Sheet 1 — Universo */}
            <div className="floating-sheet-1 css-3d-paper absolute top-1/4 left-1/3 w-[260px] h-[340px] glass-panel p-6 flex flex-col justify-between text-left opacity-0 pointer-events-auto rounded-lg shadow-2xl border-l-2 border-l-luna-gold/30">
              <div className="flex justify-between items-start border-b border-luna-lavender/10 pb-3">
                <span
                  style={{ fontFamily: "var(--landing-font-mono)" }}
                  className="text-[10px] tracking-widest text-luna-gold"
                >
                  STORY BIBLE
                </span>
                <Feather className="w-4 h-4 text-luna-gold/60" />
              </div>
              <div className="space-y-2">
                <span
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-sm font-medium text-white block"
                >
                  {t("cinematicLanding.sheet1Title", "Personagens & Lore")}
                </span>
                <p
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-[11px] leading-relaxed text-luna-lavender/80 italic"
                >
                  {t(
                    "cinematicLanding.sheet1Description",
                    "Enciclopédia viva do seu universo — personagens, locais e regras de lore que a IA consulta a cada sugestão, mantendo coerência do início ao fim."
                  )}
                </p>
              </div>
              <div
                style={{ fontFamily: "var(--landing-font-mono)" }}
                className="flex justify-between items-center text-[10px] text-luna-lavender/40 pt-3 border-t border-luna-lavender/10"
              >
                <span>{t("cinematicLanding.sheet1TagEditable", "EDITÁVEL SEMPRE")}</span>
                <span>{t("cinematicLanding.sheet1TagRespects", "IA respeita")}</span>
              </div>
            </div>

            {/* Paper Sheet 2 — Outline Board */}
            <div className="floating-sheet-2 css-3d-paper absolute bottom-1/4 left-[40%] w-[260px] h-[340px] glass-panel p-6 flex flex-col justify-between text-left opacity-0 pointer-events-auto rounded-lg shadow-2xl border-l-2 border-l-luna-glow/30">
              <div className="flex justify-between items-start border-b border-luna-lavender/10 pb-3">
                <span
                  style={{ fontFamily: "var(--landing-font-mono)" }}
                  className="text-[10px] tracking-widest text-luna-glow"
                >
                  OUTLINE BOARD
                </span>
                <Kanban className="w-4 h-4 text-luna-glow" />
              </div>
              <div className="space-y-2">
                <span
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-sm font-medium text-white block glow-text-purple"
                >
                  {t("cinematicLanding.sheet2Title", "Kanban de Cenas")}
                </span>
                <p
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-[11px] leading-relaxed text-luna-lavender/80 italic"
                >
                  {t(
                    "cinematicLanding.sheet2Description",
                    "Organize atos e capítulos em colunas arrastáveis. Veja a estrutura narrativa completa e reorganize cenas antes de escrever uma única palavra."
                  )}
                </p>
              </div>
              <div
                style={{ fontFamily: "var(--landing-font-mono)" }}
                className="flex justify-between items-center text-[10px] text-luna-lavender/40 pt-3 border-t border-luna-lavender/10"
              >
                <span>DRAG & DROP</span>
                <span>{t("cinematicLanding.sheet2TagActs", "3 atos")}</span>
              </div>
            </div>

            {/* Paper Sheet 3 — RAG / IA com contexto */}
            <div className="floating-sheet-3 css-3d-paper absolute top-1/3 left-1/4 w-[260px] h-[340px] glass-panel p-6 flex flex-col justify-between text-left opacity-0 pointer-events-auto rounded-lg shadow-2xl border-l-2 border-l-luna-lavender/30">
              <div className="flex justify-between items-start border-b border-luna-lavender/10 pb-3">
                <span
                  style={{ fontFamily: "var(--landing-font-mono)" }}
                  className="text-[10px] tracking-widest text-luna-lavender"
                >
                  {t("cinematicLanding.sheet3Tag", "IA COM RAG")}
                </span>
                <Brain className="w-4 h-4 text-luna-lavender" />
              </div>
              <div className="space-y-2">
                <span
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-sm font-medium text-white block"
                >
                  {t("cinematicLanding.sheet3Title", "IA que leu seu livro")}
                </span>
                <p
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-[11px] leading-relaxed text-luna-lavender/80 italic"
                >
                  {t(
                    "cinematicLanding.sheet3Description",
                    "Retrieval-Augmented Generation sobre o próprio manuscrito — o assistente busca trechos reais do seu texto para sugerir continuações coerentes com a sua voz."
                  )}
                </p>
              </div>
              <div
                style={{ fontFamily: "var(--landing-font-mono)" }}
                className="flex justify-between items-center text-[10px] text-luna-lavender/40 pt-3 border-t border-luna-lavender/10"
              >
                <span>GPT-4O-MINI</span>
                <span>{t("cinematicLanding.sheet3TagContext", "Contexto total")}</span>
              </div>
            </div>
          </div>

          {/* =========================================================================
              SCENE 3: CONSTELLATIONS & CONVERGENCE (30% - 48%)
              ========================================================================= */}
          <svg className="constellation-svg absolute inset-0 w-full h-full pointer-events-none z-10 opacity-100">
            <path
              className="constellation-path"
              d="M 280, 240 Q 420, 180 580, 320 T 960, 280"
              fill="none"
              stroke="rgba(252, 246, 189, 0.45)"
              strokeWidth="2"
              strokeDasharray="12 6"
              strokeDashoffset="1200"
            />
            <path
              d="M 280, 480 Q 460, 400 580, 320"
              fill="none"
              stroke="rgba(161, 140, 209, 0.35)"
              strokeWidth="1.5"
              strokeDasharray="8 4"
            />
          </svg>

          {/* The AI glowing spark running along the path */}
          <div className="constellation-spark absolute w-4 h-4 rounded-full bg-luna-gold shadow-[0_0_15px_rgba(252,246,189,1)] z-20 pointer-events-none opacity-0" />

          {/* Scene 3 Narrative box on the right */}
          <div className="scene3-narrative-box absolute right-[8%] md:right-[15%] max-w-md text-left z-30 opacity-0 pointer-events-none">
            <span
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="text-xs tracking-[0.25em] text-luna-gold block mb-3 uppercase"
            >
              {t("cinematicLanding.scene3Eyebrow", "A IA que conhece o seu livro")}
            </span>
            <h2
              style={{ fontFamily: "var(--landing-font-serif)" }}
              className="text-3xl md:text-5xl text-white mb-5 leading-tight glow-text-purple"
            >
              {t("cinematicLanding.scene3Title", "Contexto que não se perde")}
            </h2>
            <p className="font-sans text-sm md:text-base leading-relaxed text-luna-lavender/70 font-light">
              <span>
                {t(
                  "cinematicLanding.scene3Paragraph1",
                  "Seu Universo — personagens, locais, lore — alimenta o modelo em cada sugestão. A IA não esquece o que você escreveu no capítulo 1 quando está no capítulo 12."
                )}
              </span>
              <br /><br />
              <span>
                {t(
                  "cinematicLanding.scene3Paragraph2",
                  "RAG sobre o próprio manuscrito garante coerência narrativa. Cada sugestão parte do que você já criou — não de textos genéricos de treinamento."
                )}
              </span>
            </p>
          </div>

          {/* =========================================================================
              SCENE 4 & 5: APP MOCKUP EDITOR (48% - 65%) + NARRATIVE REVELATION
              ========================================================================= */}
          <div className="app-editor-mockup absolute w-[90%] max-w-3xl h-[480px] glass-panel rounded-xl shadow-2xl flex flex-col overflow-hidden opacity-0 pointer-events-auto z-30 translate-y-[200px]">
            {/* Window bar */}
            <div className="h-11 px-5 border-b border-luna-lavender/10 flex items-center justify-between bg-luna-deep/60">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
                <span
                  style={{ fontFamily: "var(--landing-font-mono)" }}
                  className="ml-4 text-[10px] tracking-wider text-luna-lavender/45"
                >
                  reformu.lat — meu-livro.lat
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Maximize2 className="w-3.5 h-3.5 text-luna-lavender/40 hover:text-luna-lavender transition-colors cursor-pointer" />
              </div>
            </div>

            {/* Layout body */}
            <div className="flex-1 flex overflow-hidden">
              {/* Sidebar chapters */}
              <div className="w-48 border-r border-luna-lavender/5 bg-luna-deep/30 p-4 flex flex-col justify-between">
                <div className="space-y-4">
                  <div
                    style={{ fontFamily: "var(--landing-font-mono)" }}
                    className="flex items-center gap-2 text-xs text-luna-lavender/70 tracking-widest uppercase"
                  >
                    <Library className="w-3.5 h-3.5 text-luna-glow" />
                    {t("cinematicLanding.mockupManuscript", "Manuscrito")}
                  </div>
                  <div className="space-y-1.5 text-xs select-none">
                    <div
                      onClick={() => setActiveChapter(1)}
                      className={`px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        activeChapter === 1
                          ? "bg-luna-purple/40 text-luna-gold border-l border-l-luna-gold"
                          : "text-luna-lavender/50 hover:text-luna-lavender hover:bg-luna-lavender/5"
                      }`}
                    >
                      {t("cinematicLanding.chapterPrologue", "Prólogo")}
                    </div>
                    <div
                      onClick={() => setActiveChapter(2)}
                      className={`px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        activeChapter === 2
                          ? "bg-luna-purple/40 text-luna-gold border-l border-l-luna-gold"
                          : "text-luna-lavender/50 hover:text-luna-lavender hover:bg-luna-lavender/5"
                      }`}
                    >
                      {t("cinematicLanding.chapterOne", "Capítulo 1")}
                    </div>
                    <div
                      onClick={() => setActiveChapter(3)}
                      className={`px-3 py-1.5 rounded-md cursor-pointer transition-all ${
                        activeChapter === 3
                          ? "bg-luna-purple/40 text-luna-gold border-l border-l-luna-gold"
                          : "text-luna-lavender/50 hover:text-luna-lavender hover:bg-luna-lavender/5"
                      }`}
                    >
                      {t("cinematicLanding.chapterTwo", "Capítulo 2")}
                    </div>
                  </div>
                </div>

                <div
                  style={{ fontFamily: "var(--landing-font-mono)" }}
                  className="flex items-center gap-2 text-[10px] text-luna-gold/60"
                >
                  <History className="w-3 h-3" />
                  {t("cinematicLanding.mockupSavedVersion", "Versão salva")}
                </div>
              </div>

              {/* Main writing area */}
              <div className="flex-1 p-6 text-left overflow-y-auto no-scrollbar flex flex-col justify-between relative bg-luna-dark/20">
                <div className="space-y-4">
                  <div
                    style={{ fontFamily: "var(--landing-font-serif)" }}
                    className="text-lg text-white mb-2 pb-1 border-b border-luna-lavender/5"
                  >
                    {activeChapter === 1
                      ? t("cinematicLanding.chapterPrologue", "Prólogo")
                      : activeChapter === 2
                      ? t("cinematicLanding.chapterOne", "Capítulo 1")
                      : t("cinematicLanding.chapterTwo", "Capítulo 2")}
                  </div>

                  {activeChapter === 1 ? (
                    <p
                      style={{ fontFamily: "var(--landing-font-serif)" }}
                      className="text-xs md:text-sm leading-relaxed text-luna-lavender/85 relative"
                    >
                      {typedText}
                      <span className="w-1.5 h-4 bg-luna-gold inline-block ml-0.5 animate-pulse absolute bottom-0.5" />
                    </p>
                  ) : (
                    <p
                      style={{ fontFamily: "var(--landing-font-serif)" }}
                      className="text-xs md:text-sm leading-relaxed text-luna-lavender/60 italic"
                    >
                      {activeChapter === 2
                        ? t(
                            "cinematicLanding.chapterOneSample",
                            "O personagem avançou pelo corredor vazio, cada passo ecoando contra o mármore frio. Havia algo errado naquele silêncio — não a ausência de som, mas a presença de algo que preferia não ser ouvido..."
                          )
                        : t(
                            "cinematicLanding.chapterTwoSample",
                            "A conversa havia terminado antes de começar. Ela fechou o caderno e percebeu que não precisava mais das anotações — a história já vivia dentro dela, esperando apenas o momento certo para ser contada."
                          )}
                    </p>
                  )}

                  {storyContext.map((item, idx) => (
                    <p
                      key={idx}
                      style={{ fontFamily: "var(--landing-font-serif)" }}
                      className={`text-xs md:text-sm leading-relaxed p-2.5 rounded-lg border ${
                        item.startsWith("Autor:")
                          ? "text-luna-gold/90 bg-luna-purple/10 border-luna-gold/15 ml-4"
                          : "text-white bg-luna-glow/10 border-luna-glow/15 mr-4"
                      }`}
                    >
                      <strong
                        style={{ fontFamily: "var(--landing-font-mono)" }}
                        className="text-[9px] uppercase tracking-wider block mb-1"
                      >
                        {item.split(":")[0]}
                      </strong>
                      {item.substring(item.indexOf(":") + 1).trim()}
                    </p>
                  ))}
                </div>

                {/* AI live suggestions box integrated inside editor */}
                <div className="mt-8 pt-4 border-t border-luna-lavender/5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-luna-gold animate-ping" />
                    <span
                      style={{ fontFamily: "var(--landing-font-mono)" }}
                      className="text-[10px] uppercase tracking-widest text-luna-gold"
                    >
                      {t("cinematicLanding.assistantActive", "Assistente de escrita ativo")}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userPrompt}
                      onChange={(e) => setUserPrompt(e.target.value)}
                      placeholder={t("cinematicLanding.assistantInputPlaceholder", "O que acontece a seguir? Peça uma sugestão à IA...")}
                      className="flex-1 bg-luna-deep/70 border border-luna-lavender/10 rounded-md px-3.5 py-1.5 text-xs text-white placeholder-luna-lavender/40 focus:outline-none focus:border-luna-gold/45 transition-colors"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAiInspiration();
                      }}
                    />
                    <button
                      onClick={handleAiInspiration}
                      disabled={isAiLoading || !userPrompt.trim()}
                      style={{ fontFamily: "var(--landing-font-mono)" }}
                      className="px-4 py-1.5 rounded-md bg-gradient-to-r from-luna-purple to-luna-glow border border-luna-gold/25 text-[11px] text-white tracking-wider uppercase flex items-center gap-2 hover:opacity-90 active:scale-95 disabled:opacity-40 disabled:scale-100 transition-all cursor-pointer shadow-lg shadow-luna-purple/50"
                    >
                      {isAiLoading ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5 text-luna-gold" />
                      )}
                      {t("cinematicLanding.assistantButton", "Inspirar")}
                    </button>
                  </div>

                  {aiResponse && (
                    <div className="mt-3.5 p-3 rounded-lg bg-luna-purple/30 border border-luna-gold/20 text-left relative animate-fade-in">
                      <div
                        style={{ fontFamily: "var(--landing-font-mono)" }}
                        className="text-[9px] text-luna-gold tracking-widest uppercase mb-1"
                      >
                        {t("cinematicLanding.assistantResponseLabel", "Sugestão Gerada por IA")}
                      </div>
                      <p
                        style={{ fontFamily: "var(--landing-font-serif)" }}
                        className="text-xs leading-relaxed text-white italic"
                      >
                        {aiResponse}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Floating AI Aura box accompanying the mockup */}
          <div className="editor-ai-suggestion-box absolute right-[5%] top-1/4 max-w-xs text-left glass-panel p-5 rounded-lg border-t-2 border-t-luna-gold/40 shadow-2xl opacity-0 pointer-events-none z-30">
            <div
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="flex items-center gap-2 text-[10px] tracking-widest text-luna-gold uppercase mb-2"
            >
              <Sparkles className="w-3.5 h-3.5 text-luna-gold animate-pulse" />
              {t("cinematicLanding.auraTitle", "Sugestão com contexto do livro")}
            </div>
            <p
              style={{ fontFamily: "var(--landing-font-serif)" }}
              className="text-xs leading-relaxed text-luna-lavender/85 italic"
            >
              {aiPresetSuggestion}
            </p>
            <div
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="mt-3 text-[9px] text-luna-lavender/40 text-right uppercase"
            >
              {t("cinematicLanding.auraFooter", "RAG ativo • Universo consultado")}
            </div>
          </div>

          {/* =========================================================================
              SCENE 5: O LIVRO LUNAR 3D CAPTION (60% - 75%)
              ========================================================================= */}
          <div className="scene5-poetic-title absolute left-[8%] md:left-[12%] max-w-sm text-left z-30 opacity-0 pointer-events-none">
            <span
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="text-xs tracking-[0.25em] text-luna-gold block mb-3 uppercase"
            >
              {t("cinematicLanding.scene5Eyebrow", "Do manuscrito ao livro publicado")}
            </span>
            <h2
              style={{ fontFamily: "var(--landing-font-serif)" }}
              className="text-3xl md:text-5xl text-white mb-5 leading-tight glow-text-purple"
            >
              {t("cinematicLanding.scene5Title", "Pronto para o mundo ler")}
            </h2>
            <p className="font-sans text-sm md:text-base leading-relaxed text-luna-lavender/70 font-light">
              {t("cinematicLanding.scene5Paragraph1", "Quando o manuscrito estiver pronto, exporte em PDF print-ready, EPUB ou DOCX com um clique.")}
              <br /><br />
              {t(
                "cinematicLanding.scene5Paragraph2",
                "Gere a capa com IA ou contrate um designer no marketplace integrado. Seu livro sai da tela para as mãos do leitor sem fricção."
              )}
            </p>
          </div>

          {/* =========================================================================
              SCENE 6: BENTO DE BENEFÍCIOS (75% - 90%)
              ========================================================================= */}
          <div className="benefits-main-container absolute inset-x-0 top-1/2 -translate-y-1/2 max-w-5xl mx-auto px-6 text-center z-30 opacity-100 pointer-events-none">
            <div className="benefits-heading space-y-3 mb-10 opacity-0 select-none">
              <span
                style={{ fontFamily: "var(--landing-font-mono)" }}
                className="text-xs tracking-[0.3em] text-luna-gold block uppercase"
              >
                {t("cinematicLanding.benefitsEyebrow", "Tudo que um livro precisa")}
              </span>
              <h2
                style={{ fontFamily: "var(--landing-font-serif)" }}
                className="text-4xl md:text-6xl text-white glow-text-gold"
              >
                {t("cinematicLanding.benefitsTitle", "Uma plataforma, o livro inteiro")}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pointer-events-auto text-left select-none">
              {/* Card 1 — IA de escrita com RAG */}
              <div className="benefit-item-1 benefit-card glass-panel p-6 rounded-xl border-t border-t-luna-glow/20 flex flex-col justify-between hover:scale-[1.02] hover:border-t-luna-glow/50 transition-all group shadow-lg min-h-[220px]">
                <div className="w-10 h-10 rounded-lg bg-luna-purple/50 border border-luna-glow/10 flex items-center justify-center text-luna-glow group-hover:scale-110 group-hover:bg-luna-purple transition-all shadow-md">
                  <Brain className="w-5 h-5 text-luna-glow" />
                </div>
                <div className="space-y-2 mt-4">
                  <h3
                    style={{ fontFamily: "var(--landing-font-serif)" }}
                    className="text-lg text-white font-medium group-hover:text-luna-gold transition-colors"
                  >
                    {t("cinematicLanding.benefitCard1Title", "IA de escrita + RAG")}
                  </h3>
                  <p className="font-sans text-xs leading-relaxed text-luna-lavender/65 font-light">
                    {t(
                      "cinematicLanding.benefitCard1Description",
                      "O assistente lê o seu manuscrito inteiro antes de sugerir. Cada continuação respeita a sua voz, seus personagens e o arco narrativo já construído."
                    )}
                  </p>
                </div>
              </div>

              {/* Card 2 — Universo & Lore */}
              <div className="benefit-item-2 benefit-card glass-panel p-6 rounded-xl border-t border-t-luna-gold/20 flex flex-col justify-between hover:scale-[1.02] hover:border-t-luna-gold/50 transition-all group shadow-lg min-h-[220px]">
                <div className="w-10 h-10 rounded-lg bg-luna-purple/50 border border-luna-gold/10 flex items-center justify-center text-luna-gold group-hover:scale-110 group-hover:bg-luna-purple transition-all shadow-md">
                  <Compass className="w-5 h-5 text-luna-gold" />
                </div>
                <div className="space-y-2 mt-4">
                  <h3
                    style={{ fontFamily: "var(--landing-font-serif)" }}
                    className="text-lg text-white font-medium group-hover:text-luna-gold transition-colors"
                  >
                    {t("cinematicLanding.benefitCard2Title", "Universo & Lore")}
                  </h3>
                  <p className="font-sans text-xs leading-relaxed text-luna-lavender/65 font-light">
                    {t(
                      "cinematicLanding.benefitCard2Description",
                      "Personagens, locais e regras do universo em fichas editáveis. A IA consulta tudo isso ao escrever — sem contradições, sem esquecimentos."
                    )}
                  </p>
                </div>
              </div>

              {/* Card 3 — Capas com IA & Marketplace */}
              <div className="benefit-item-3 benefit-card glass-panel p-6 rounded-xl border-t border-t-luna-glow/20 flex flex-col justify-between hover:scale-[1.02] hover:border-t-luna-glow/50 transition-all group shadow-lg min-h-[220px]">
                <div className="w-10 h-10 rounded-lg bg-luna-purple/50 border border-luna-glow/10 flex items-center justify-center text-luna-glow group-hover:scale-110 group-hover:bg-luna-purple transition-all shadow-md">
                  <Sparkles className="w-5 h-5 text-luna-glow" />
                </div>
                <div className="space-y-2 mt-4">
                  <h3
                    style={{ fontFamily: "var(--landing-font-serif)" }}
                    className="text-lg text-white font-medium group-hover:text-luna-gold transition-colors"
                  >
                    {t("cinematicLanding.benefitCard3Title", "Capas com IA & Marketplace")}
                  </h3>
                  <p className="font-sans text-xs leading-relaxed text-luna-lavender/65 font-light">
                    {t(
                      "cinematicLanding.benefitCard3Description",
                      "Gere capas profissionais com IA ou conecte-se a designers, revisores e ghostwriters no marketplace integrado — tudo à la carte."
                    )}
                  </p>
                </div>
              </div>

              {/* Card 4 — Export print-ready & Colaboração */}
              <div className="benefit-item-4 benefit-card glass-panel p-6 rounded-xl border-t border-t-luna-lavender/25 flex flex-col justify-between hover:scale-[1.02] hover:border-t-luna-lavender transition-all group shadow-lg min-h-[220px]">
                <div className="w-10 h-10 rounded-lg bg-luna-purple/50 border border-luna-lavender/10 flex items-center justify-center text-luna-lavender group-hover:scale-110 group-hover:bg-luna-purple transition-all shadow-md">
                  <FileOutput className="w-5 h-5 text-luna-lavender" />
                </div>
                <div className="space-y-2 mt-4">
                  <h3
                    style={{ fontFamily: "var(--landing-font-serif)" }}
                    className="text-lg text-white font-medium group-hover:text-luna-gold transition-colors"
                  >
                    {t("cinematicLanding.benefitCard4Title", "Export & Colaboração")}
                  </h3>
                  <p className="font-sans text-xs leading-relaxed text-luna-lavender/65 font-light">
                    {t(
                      "cinematicLanding.benefitCard4Description",
                      "PDF print-ready, EPUB e DOCX com um clique. Compartilhe o projeto com coautores ou beta-leitores e colabore em tempo real."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* =========================================================================
              SCENE 7: ECLIPSE FINAL & CTA DE INSCRIÇÃO (90% - 100%)
              ========================================================================= */}
          <div className="final-cta-card absolute w-[90%] max-w-xl glass-panel p-8 md:p-12 rounded-2xl shadow-3xl opacity-0 pointer-events-auto border-t-2 border-t-luna-gold/40 text-center z-40 select-none">
            <div
              style={{ fontFamily: "var(--landing-font-mono)" }}
              className="inline-flex items-center gap-2 px-3 py-1 bg-luna-purple/40 border border-luna-gold/25 rounded-full text-[10px] text-luna-gold tracking-widest uppercase mb-6 shadow-md animate-float-slow"
            >
              <Star className="w-3 h-3 text-luna-gold animate-pulse" />
              {t("cinematicLanding.freeTierBadge", "Grátis para começar")}
            </div>

            {!isBetaSubmitted ? (
              <div className="space-y-6">
                <h2
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-3xl md:text-5xl text-white tracking-tight glow-text-gold"
                >
                  {t("cinematicLanding.ctaTitle", "Comece a escrever de graça")}
                </h2>

                <p className="font-sans text-sm text-luna-lavender/70 leading-relaxed font-light max-w-md mx-auto">
                  {t(
                    "cinematicLanding.ctaSubtitle",
                    "Editor completo, IA com RAG, Universo e Outline Board — sem cartão de crédito. Só o seu livro esperando para começar."
                  )}
                </p>

                {/* Primary CTA */}
                <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate("/app")}
                    style={{ fontFamily: "var(--landing-font-mono)" }}
                    className="px-8 py-3.5 rounded-lg bg-gradient-to-r from-luna-gold to-white border border-luna-gold text-sm font-semibold text-luna-dark tracking-widest uppercase flex items-center justify-center gap-2 hover:opacity-90 active:scale-95 transition-all cursor-pointer shadow-lg shadow-luna-gold/20"
                  >
                    {t("cinematicLanding.ctaPrimaryButton", "Começar grátis")}
                    <ArrowRight className="w-4 h-4 text-luna-dark" />
                  </button>
                  <button
                    onClick={() => navigate("/pricing")}
                    style={{ fontFamily: "var(--landing-font-mono)" }}
                    className="px-8 py-3.5 rounded-lg border border-luna-lavender/20 bg-luna-deep/40 text-sm text-luna-lavender/80 tracking-widest uppercase hover:border-luna-gold/40 hover:text-luna-gold transition-all cursor-pointer"
                  >
                    {t("cinematicLanding.ctaSecondaryButton", "Ver preços")}
                  </button>
                </div>

                {/* Optional email capture */}
                <form onSubmit={handleBetaSubmit} className="space-y-2 pt-2 border-t border-luna-lavender/10">
                  <span
                    style={{ fontFamily: "var(--landing-font-mono)" }}
                    className="text-[10px] text-luna-lavender/40 block"
                  >
                    {t("cinematicLanding.ctaEmailCaption", "Ou receba novidades por e-mail (opcional)")}
                  </span>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder={t("cinematicLanding.ctaEmailPlaceholder", "seu@email.com")}
                      className="flex-1 bg-luna-deep/70 border border-luna-lavender/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-luna-lavender/30 focus:outline-none focus:border-luna-gold/50 focus:ring-1 focus:ring-luna-gold/25 transition-all"
                    />
                    <button
                      type="submit"
                      style={{ fontFamily: "var(--landing-font-mono)" }}
                      className="px-5 py-2.5 rounded-lg border border-luna-lavender/20 bg-luna-deep/40 text-xs text-luna-lavender/70 tracking-wider uppercase hover:border-luna-gold/30 hover:text-luna-gold transition-all cursor-pointer"
                    >
                      {t("cinematicLanding.ctaSubscribeButton", "Inscrever")}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="py-6 space-y-4 animate-fade-in text-center flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-luna-gold/10 border border-luna-gold/30 flex items-center justify-center mb-2 shadow-inner">
                  <CheckCircle2 className="w-8 h-8 text-luna-gold animate-pulse" />
                </div>
                <h3
                  style={{ fontFamily: "var(--landing-font-serif)" }}
                  className="text-2xl text-white glow-text-gold"
                >
                  {t("cinematicLanding.ctaSuccessTitle", "Inscrito com sucesso")}
                </h3>
                <p className="font-sans text-sm text-luna-lavender/70 max-w-sm mx-auto leading-relaxed font-light">
                  {t("cinematicLanding.ctaSuccessMessage", "Vamos te avisar quando houver novidades.")}{" "}
                  <span className="text-white font-mono">{emailInput}</span>
                </p>
                <button
                  onClick={() => navigate("/app")}
                  style={{ fontFamily: "var(--landing-font-mono)" }}
                  className="mt-4 px-8 py-3 rounded-lg bg-gradient-to-r from-luna-gold to-white text-sm font-semibold text-luna-dark tracking-widest uppercase flex items-center gap-2 hover:opacity-90 cursor-pointer"
                >
                  {t("cinematicLanding.ctaSuccessButton", "Ir para o editor")}
                  <ArrowRight className="w-4 h-4 text-luna-dark" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Premium subtle bottom details */}
      <footer className="relative w-full py-8 border-t border-luna-lavender/5 z-40 bg-luna-dark text-center select-none">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              style={{ fontFamily: "var(--landing-font-serif)" }}
              className="tracking-widest text-sm text-luna-lavender/50 uppercase"
            >
              reformu.lat
            </span>
            <span className="text-xs text-luna-lavender/30">{t("cinematicLanding.footerTagline", "• Plataforma de criação literária com IA")}</span>
          </div>
          <div
            style={{ fontFamily: "var(--landing-font-mono)" }}
            className="text-[10px] text-luna-lavender/40 tracking-wider"
          >
            {t("cinematicLanding.footerCopyright", "reformu.lat © 2026. Todos os direitos reservados.")}
          </div>
          <div
            style={{ fontFamily: "var(--landing-font-mono)" }}
            className="flex items-center gap-4 text-xs text-luna-lavender/45"
          >
            <span
              onClick={() => navigate("/pricing")}
              className="hover:text-luna-gold transition-colors cursor-pointer"
            >
              {t("cinematicLanding.navPricing", "Preços")}
            </span>
            <span
              onClick={() => navigate("/marketplace")}
              className="hover:text-luna-gold transition-colors cursor-pointer"
            >
              {t("cinematicLanding.navMarketplace", "Marketplace")}
            </span>
            <span className="hover:text-luna-gold transition-colors cursor-pointer">{t("cinematicLanding.footerPrivacy", "Privacidade")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
